import {useEffect, useRef} from "react";

// Comportement commun des boîtes de dialogue modales (Modal, lightbox vidéo) — attendu d'un service public (RGAA) :
//  - Échap ferme, mais seulement la boîte du dessus ;
//  - le focus clavier est piégé dans la boîte du dessus (Tab / Maj+Tab bouclent, un focus qui s'échappe y est ramené) ;
//  - tout le reste de la page est inerte (`inert`) et masqué aux lecteurs d'écran (`aria-hidden`) tant qu'une boîte est ouverte ;
//    un élément marqué `data-inert-exempt` (ex. notification) reste actif ;
//  - le défilement de la page est bloqué ;
//  - à la fermeture, le focus revient à l'élément qui avait ouvert la boîte.

const dialogs = []; // pile des boîtes ouvertes ; la dernière est celle du dessus : {token, ref}
const inerted = new Map(); // élément rendu inerte -> états d'origine, pour les rétablir à l'identique
let savedOverflow = null;

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

const focusablesIn = (panel) => Array.from(panel.querySelectorAll(FOCUSABLE));
const topDialog = () => dialogs[dialogs.length - 1];

function restoreBackground() {
    for (const [el, previous] of inerted) {
        if (!previous.inert) el.removeAttribute("inert");
        if (previous.ariaHidden === null) el.removeAttribute("aria-hidden");
        else el.setAttribute("aria-hidden", previous.ariaHidden);
    }
    inerted.clear();
}

// Rend inerte tout ce qui n'est pas sur le chemin entre la boîte du dessus et <body> : le reste de la page, mais aussi les
// boîtes du dessous.
function syncBackground() {
    restoreBackground();
    const top = topDialog();
    let node = top && top.ref.current;
    while (node && node !== document.body && node.parentElement) {
        for (const sibling of node.parentElement.children) {
            if (sibling === node || sibling.hasAttribute("data-inert-exempt") || /^(SCRIPT|STYLE|LINK)$/.test(sibling.tagName)) continue;
            inerted.set(sibling, {inert: sibling.hasAttribute("inert"), ariaHidden: sibling.getAttribute("aria-hidden")});
            sibling.setAttribute("inert", "");
            sibling.setAttribute("aria-hidden", "true");
        }
        node = node.parentElement;
    }
}

function lockScroll() {
    if (dialogs.length === 1) {
        savedOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
    }
}

function unlockScroll() {
    if (dialogs.length === 0 && savedOverflow !== null) {
        document.body.style.overflow = savedOverflow;
        savedOverflow = null;
    }
}

// dialogRef : l'élément qui porte role="dialog" (il doit avoir tabIndex={-1} pour pouvoir recevoir le focus).
export default function useDialogBehavior(dialogRef, onClose, initialFocusRef) {
    const closeRef = useRef(onClose);
    closeRef.current = onClose;

    useEffect(() => {
        const token = {};
        const trigger = document.activeElement;
        dialogs.push({token, ref: dialogRef});
        lockScroll();
        syncBackground();
        (initialFocusRef?.current ?? dialogRef.current)?.focus();

        const isTop = () => topDialog() && topDialog().token === token;

        function handleKeyDown(e) {
            if (!isTop()) return;
            if (e.key === "Escape") {
                closeRef.current();
                return;
            }
            if (e.key !== "Tab") return;
            const panel = dialogRef.current;
            if (!panel) return;
            const items = focusablesIn(panel);
            const active = document.activeElement;
            if (items.length === 0) {
                e.preventDefault();
                panel.focus();
                return;
            }
            const first = items[0];
            const last = items[items.length - 1];
            const outside = !panel.contains(active) || active === panel;
            if (e.shiftKey) {
                if (active === first || outside) { e.preventDefault(); last.focus(); }
            } else if (active === last || outside) {
                e.preventDefault();
                first.focus();
            }
        }

        // Un focus qui part vers l'arrière-plan (souris, script) est ramené dans la boîte du dessus.
        function handleFocus(e) {
            const panel = dialogRef.current;
            if (!isTop() || !panel || panel.contains(e.target)) return;
            (focusablesIn(panel)[0] || panel).focus();
        }

        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("focus", handleFocus, true);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("focus", handleFocus, true);
            const index = dialogs.findIndex(d => d.token === token);
            if (index !== -1) dialogs.splice(index, 1);
            syncBackground();
            unlockScroll();
            // Après avoir rétabli la page : l'élément déclencheur n'est plus inerte, il peut reprendre le focus.
            trigger?.focus?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
